export type RuntimeEndFinalizationScope = {
  membershipKey: string;
  studentKey: string;
  sessionId: string;
};

type PendingFinalization = RuntimeEndFinalizationScope & {
  promise: Promise<unknown>;
};

const pendingByStudent = new Map<string, PendingFinalization>();

function scopeKey(membershipKey: string, studentKey: string): string {
  return `${membershipKey}:${studentKey}`;
}

/**
 * Register an in-flight Runtime End for the current student lifecycle.
 * Clears automatically when the promise settles (success or failure).
 */
export function registerRuntimeEndFinalization(
  scope: RuntimeEndFinalizationScope,
  promise: Promise<unknown>,
): void {
  const key = scopeKey(scope.membershipKey, scope.studentKey);
  const tracked = promise.finally(() => {
    const current = pendingByStudent.get(key);
    if (current?.sessionId === scope.sessionId) {
      pendingByStudent.delete(key);
    }
  });
  pendingByStudent.set(key, { ...scope, promise: tracked });
}

/** Returns true when a Runtime End finalization is registered for this student. */
export function hasRuntimeEndFinalization(
  membershipKey: string,
  studentKey: string,
): boolean {
  return pendingByStudent.has(scopeKey(membershipKey, studentKey));
}

/**
 * Wait for an in-flight Runtime End to settle before fetching authoritative quota.
 * No-op when no finalization is registered for the student.
 */
export async function awaitRuntimeEndFinalizationIfPresent(
  membershipKey: string,
  studentKey: string,
): Promise<void> {
  const entry = pendingByStudent.get(scopeKey(membershipKey, studentKey));
  if (!entry) return;
  try {
    await entry.promise;
  } catch {
    // End failure must not block quota forever; authoritative fetch follows.
  }
}

/** Test-only reset. */
export function __clearRuntimeEndFinalizationForTests(): void {
  pendingByStudent.clear();
}
